import { useId } from 'react'
import Field, { type FieldProps } from '../field'
import {
	SuperSelect,
	SuperSelectSearchInputProps,
	type SuperSelectOptionProps,
} from './index'

interface SuperSelectFieldProps
	extends Omit<FieldProps, 'children' | 'id' | 'layout'> {
	id?: string
	options: SuperSelectOptionProps[]
	/**
	 * @default false
	 */
	searchProps?: SuperSelectSearchInputProps
	/**
	 * Used for assistive technologies,
	 * indicate the category of options you are presenting
	 * eg. Schools | Languages | Countries
	 */
	listLabel: string
	/**
	 * Is actually a `max-height` for the list.
	 * Pass this when you have a lot of options and want to limit the height of the list.
	 * @default `240px`
	 */
	listHeight?: `${number}em` | `${number}px` | `${number}%`
	/**
	 * Callback for when the list is scrolled.
	 * This is useful for implementing pagination, recommended for large lists.
	 */
	onScroll?: (e: React.UIEvent<HTMLUListElement>) => void
	onChangeSelectedOption?: (option: SuperSelectOptionProps) => void
	onChangeVFocusedOption?: (option: SuperSelectOptionProps) => void
	onSearchBlur?: () => void
}

/**
 * Convenience component for using {@link SuperSelect}
 * with a {@link Field} component. This should cover most use cases
 * unless you need advanced patterns like pagination, async options, etc. outside of a form context.
 */
const SuperSelectField = ({
	label,
	helperText,
	error,
	id,
	searchProps,
	options,
	listLabel,
	listHeight = '240px',
	onScroll,
	onChangeSelectedOption,
	onChangeVFocusedOption,
	onSearchBlur,
	...rest
}: SuperSelectFieldProps) => {
	const genID = useId()
	const fieldId = id ?? genID

	return (
		<Field
			label={label}
			helperText={helperText}
			error={error}
			id={fieldId}
			layout={'vertical'}
			{...rest}
		>
			<SuperSelect.Root
				onChangeSelectedOption={onChangeSelectedOption}
				onChangeVFocusedOption={onChangeVFocusedOption}
			>
				<SuperSelect.Trigger />
				<SuperSelect.Popover>
					{searchProps && (
						<SuperSelect.SearchInput
							placeholder={searchProps.placeholder}
							value={searchProps.value}
							onChange={searchProps.onChange}
							onBlur={onSearchBlur}
						/>
					)}
					<SuperSelect.List
						label={listLabel}
						maxHeight={listHeight}
						onScroll={onScroll}
					>
						{options.map((option) => (
							<SuperSelect.Option key={option.value} {...option} />
						))}
					</SuperSelect.List>
				</SuperSelect.Popover>
			</SuperSelect.Root>
		</Field>
	)
}

export default SuperSelectField
